import { v4 as uuidv4 } from "uuid";

import { supabase } from "../../../lib/supabase";
import { addParticipant, createMeeting as createDyteMeeting } from "./dyte";

export interface Appointment {
  id: string;
  mentorName: string;
  mentorEmail: string;
  dateTime: string;
  duration: number;
  status: string;
  meetingLink?: string;
}

interface RawMenteeData {
  uuid: string;
  user_uuid: string;
  vincer_base_user: {
    name: string;
    email: string;
  };
}

/**
 * Creates a new meeting
 */
export async function createMeeting(
  mentorUuid: string,
  scheduledTime: string,
  duration: number = 60
) {
  try {
    console.log("Starting meeting creation process...", {
      mentorUuid,
      scheduledTime,
      duration,
    });

    // First get the current mentee
    console.log("Fetching current mentee...");
    const mentee = await fetchCurrentMentee();
    console.log("Mentee fetched:", {
      menteeId: mentee.uuid,
      menteeName: mentee.name,
    });

    console.log("Fetching mentor details...");
    const mentor = await fetchMentor(mentorUuid);
    console.log("Mentor fetched:", {
      mentorId: mentor.uuid,
      mentorName: mentor.name,
    });

    const menteeName = mentee.name;
    const mentorName = mentor.name;

    console.log("Creating Dyte meeting...");
    const dyteMeeting = await createDyteMeeting(
      `${menteeName}'s session with ${mentorName}`
    );
    console.log("Dyte meeting response:", dyteMeeting);

    if (dyteMeeting.status === "error") {
      console.error("Error creating dyte meeting:", dyteMeeting.message);
      throw new Error("Error creating dyte meeting");
    }

    console.log("Adding mentor participant to Dyte meeting...");
    const mentorParticipant = await addParticipant(
      dyteMeeting.data.id,
      mentor.uuid,
      mentorName,
      "group_call_host"
    );
    console.log("Mentor participant added:", mentorParticipant);

    console.log("Adding mentee participant to Dyte meeting...");
    const menteeParticipant = await addParticipant(
      dyteMeeting.data.id,
      mentee.uuid,
      menteeName,
      "group_call_participant"
    );
    console.log("Mentee participant added:", menteeParticipant);

    const menteeAuthToken = menteeParticipant.data.token;
    const mentorAuthToken = mentorParticipant.data.token;

    // Generate a UUID for the meeting
    const meetingUuid = uuidv4();
    console.log("Generated meeting UUID:", meetingUuid);

    console.log("Creating meeting record in Supabase...");
    const { data, error } = await supabase
      .from("meeting")
      .insert({
        uuid: meetingUuid,
        mentor_uuid: mentorUuid,
        mentee_uuid: mentee.uuid,
        scheduled_time: scheduledTime,
        duration: duration,
        status: "pending",
        mentor_auth_token: mentorAuthToken,
        mentee_auth_token: menteeAuthToken,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating meeting in Supabase:", error);
      throw error;
    }

    console.log("Meeting created successfully:", data);
    return data;
  } catch (error) {
    console.error("Error in createMeeting:", error);
    if (error instanceof Error) {
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
    }
    throw error;
  }
}

/**
 * Fetches the current mentee record for the logged-in user
 */
export async function fetchCurrentMentee() {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("mentee")
      .select(
        `
        uuid,
        user_uuid,
        vincer_base_user (
          name,
          email
        )
      `
      )
      .eq("user_uuid", user.id)
      .single();

    if (error) {
      console.error("Error fetching mentee:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Mentee record not found");
    }

    const typedData = data as unknown as RawMenteeData;

    return {
      uuid: typedData.uuid,
      userId: typedData.user_uuid,
      name: typedData.vincer_base_user.name,
      email: typedData.vincer_base_user.email,
    };
  } catch (error) {
    console.error("Error in fetchCurrentMentee:", error);
    throw error;
  }
}

/**
 * Updates the status of an appointment
 */
export async function updateAppointmentStatus(
  appointmentId: string,
  status: string
) {
  try {
    const { error } = await supabase
      .from("meeting")
      .update({ status })
      .eq("uuid", appointmentId);

    if (error) {
      console.error("Error updating appointment status:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in updateAppointmentStatus:", error);
    throw error;
  }
}

async function fetchMentor(mentorUuid: string) {
  const { data, error } = await supabase
    .from("mentor")
    .select("*")
    .eq("uuid", mentorUuid)
    .single();

  if (error) {
    console.error("Error fetching mentor:", error);
    throw error;
  }

  const mentorUserId = data.user_uuid;

  const { data: userData, error: err } = await supabase
    .from("vincer_base_user")
    .select("*")
    .eq("uuid", mentorUserId)
    .single();

  if (err) {
    console.error("Error fetching user data:", err);
    throw err;
  }

  return userData;
}

export async function getUserAuthTokenForMeeting(
  meetingId: string,
  role: string
) {
  let authTokenName = "";
  if (role === "mentor") {
    authTokenName = "mentor_auth_token";
  } else {
    authTokenName = "mentee_auth_token";
  }

  const { data, error } = await supabase
    .from("meeting")
    .select(authTokenName)
    .eq("uuid", meetingId)
    .single();

  if (error) {
    console.error("Error fetching auth token:", error);
    throw error;
  }

  // @ts-expect-error - data is a string
  return data[authTokenName];
}
